import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, ShieldCheck } from "lucide-react";

import { getVisualDirectionVnextFoundation } from "../../lib/brandStudioApi";

const GROUP_LABELS = {
  color: "Цвет",
  typography: "Типографика",
  materiality: "Материалы",
  photography: "Фотография",
  graphic_language: "Графический язык",
  composition: "Композиция",
  applications: "Носители",
};

export default function VisualDirectionVnextFoundationOwnerPanelV1() {
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setStatus("loading");
    setError("");
    try {
      setData(await getVisualDirectionVnextFoundation());
      setStatus("ready");
    } catch (problem) {
      const detail = problem?.response?.data?.detail;
      const code =
        typeof detail === "object" && detail
          ? detail.code
          : typeof detail === "string"
            ? detail
            : "";
      setError(code || "Не удалось собрать VD1 foundation.");
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const item of data?.semantic_patterns || []) {
      if (!map[item.group]) map[item.group] = [];
      map[item.group].push(item);
    }
    return map;
  }, [data]);

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#7A684F] bg-[#FFF9F0] p-5 md:p-7"
      data-testid="visual-direction-vnext-foundation-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        OWNER QA · VD1
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Visual Direction vNext Foundation
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Read-only мост между Source Truth + VU5 и будущим Visual Direction.
        Никаких Mistral/Prodia/OpenAI вызовов на этом экране нет.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" />
          Собираю foundation из уже сохранённых данных…
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] leading-5 text-[#6B443A]">
          {error}
        </div>
      )}

      {status === "ready" && data && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              {data.ready_for_generation_contract ? <Check size={12} /> : <ShieldCheck size={12} />}
              {data.ready_for_generation_contract
                ? "Готово к Generation Contract"
                : "Нужна подготовка semantic evidence"}
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              {data.contract_version} · {data.direction_kind}
            </span>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                References
              </div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                universe: {data.references?.length || 0}<br />
                complete: {data.reference_universe_complete ? "yes" : "no"}
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Semantic
              </div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                patterns: {data.semantic_patterns?.length || 0}<br />
                cached groups: {data.cached_semantic_group_count}<br />
                missing groups: {data.missing_semantic_group_count}
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Input state
              </div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                typography anchor: {data.input_state?.typography_anchor_present ? "yes" : "no"}<br />
                concrete roles: {(data.input_state?.concrete_typography_roles || []).join(", ") || "—"}<br />
                logo: {data.input_state?.logo_state || "unset"}
              </div>
            </article>
          </div>

          {(data.explicit_decisions || []).length > 0 && (
            <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Source Truth · verbatim
              </div>
              <div className="mt-2 space-y-2">
                {data.explicit_decisions.map((item) => (
                  <div key={item.decision_id} className="text-[11.5px] leading-5 text-[#4C392B]">
                    • {item.value}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {Object.entries(grouped).map(([group, items]) => (
              <article key={group} className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
                <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                  {GROUP_LABELS[group] || group}
                </div>
                <div className="mt-2 space-y-2">
                  {items.map((item) => (
                    <div key={item.pattern_id}>
                      <div className="text-[11.5px] leading-5 text-[#4C392B]">
                        • {item.label}
                      </div>
                      <div className="text-[9.5px] text-[#8A745E]">
                        {item.strength} · refs {item.reference_ids?.length || 0}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-[#FFFCF6] p-4">
            <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
              Image-AI policy
            </div>
            <div className="mt-2 text-[11px] leading-5 text-[#655141]">
              purpose: {data.image_ai_policy?.purpose}<br />
              output count: {data.image_ai_policy?.output_count_policy}<br />
              mandatory variants: none<br />
              exact brand name: controlled renderer<br />
              exact logo: controlled renderer<br />
              exact font: controlled renderer<br />
              production files: controlled renderer
            </div>
          </div>

          <div className="mt-4 text-[10.5px] leading-5 text-[#8A745E]">
            Raw atoms, provider names, models and exact identity assets не входят в VD1 provider-neutral foundation.
          </div>
        </div>
      )}
    </section>
  );
}
